import { useEffect, useMemo, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import WorkerRecordDrawer from "../workerRecord/WorkerRecordDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useEkycReviewsList, EKYC_REVIEWS_DEFAULT_PAGE_SIZE } from "./useEkycReviewsList.js";
import { useEkycQueue, EKYC_QUEUE_PAGE_SIZE } from "./useEkycQueue.js";
import { getEkycReview, submitEkycDecision } from "./ekycReviewsApi.js";
import { startEkyc } from "../workerApplications/workerApplicationsApi.js";
import { createWorkerProfileFromEkycReview } from "../workerProfiles/workerProfilesApi.js";
import WorkerDetailedProfileDialog from "../workerDetailedProfile/WorkerDetailedProfileDialog.jsx";
import "./ekycReviewQueue.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "SuperAdmin", "Worker Intake", "e-KYC Review"];
const DRAWER_TITLE_ID = "ek-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;

// A review sitting in "Submitted" (awaiting SuperAdmin decision) longer than
// this is flagged over-SLA. This threshold, and every KPI derived from it
// below, is a client-side-only convenience over whatever page of rows is
// currently loaded - the P095 summary DTO has no dedicated over-SLA count or
// in-progress count field today (see the honest KPI mapping in the W45
// README), so this file must never claim these two numbers are real
// platform-wide totals.
const SLA_HOURS_THRESHOLD = 48;

// P149-R: this button creates the real e-KYC review record, so the label says
// so - "เริ่มตรวจ" alone read as if a record already existed.
/**
 * P149-R: the five UI states a drawer can be in. Derived once from the real
 * review status (or, when no review exists, the application status) so the
 * next-action panel, the decision block and the queue row can never disagree.
 */
const EKYC_UI_STATES = {
  Waiting: "Waiting",
  InProgress: "InProgress",
  NeedsCorrection: "NeedsCorrection",
  Approved: "Approved",
  Rejected: "Rejected",
};

function resolveDrawerState(reviewStatus, applicationStatus) {
  switch (reviewStatus) {
    case "Approved":
      return EKYC_UI_STATES.Approved;
    case "Rejected":
      return EKYC_UI_STATES.Rejected;
    case "NeedsCorrection":
      return EKYC_UI_STATES.NeedsCorrection;
    case "InProgress":
    case "Submitted":
      return EKYC_UI_STATES.InProgress;
    default:
      break;
  }

  switch (applicationStatus) {
    case "EkycApproved":
    case "WorkerProfileCreated":
      return EKYC_UI_STATES.Approved;
    case "EkycRejected":
      return EKYC_UI_STATES.Rejected;
    case "EkycNeedsCorrection":
      return EKYC_UI_STATES.NeedsCorrection;
    case "EkycInProgress":
      return EKYC_UI_STATES.InProgress;
    default:
      return EKYC_UI_STATES.Waiting;
  }
}

const OPEN_PROFILE_TEXT = "เปิด Worker Profile";
const PROFILE_NEXT_STEP_TEXT = "ขั้นต่อไปคือเติมข้อมูลแรงงานให้ครบ ก่อนจึงจะทำเครื่องหมายพร้อม Match ได้";
const NEEDS_CORRECTION_OWNER_TEXT = "ผู้รับผิดชอบ: เอเย่นต์เจ้าของสายงานต้องให้แรงงานส่งเอกสารใหม่ แล้วทีมส่วนกลางจึงตรวจต่อ";
const DOC_OWNER_WORKER_AGENT = "แรงงาน/เอเย่นต์ต้องอัปโหลดเพิ่ม";
const DOC_OWNER_BACKOFFICE = "ทีมส่วนกลางกรอกเพิ่มได้ในโปรไฟล์แรงงาน";
const APPROVE_BLOCKED_TEXT = "ยังอนุมัติไม่ได้ ข้อมูลที่จำเป็นสำหรับ e-KYC ยังไม่ครบ";

const CREATE_PROFILE_NO_REVIEW_TEXT = "ยังไม่มีเรคคอร์ด e-KYC สำหรับรายการนี้ จึงสร้าง Worker Profile ไม่ได้";
const START_EKYC_BUTTON_TEXT = "สร้างคิว e-KYC และเริ่มตรวจ";
const START_EKYC_HINT_TEXT =
  "ระบบจะสร้างเรคคอร์ด e-KYC ให้แรงงานคนนี้ แล้วเปลี่ยนสถานะเป็นกำลังตรวจ ยังไม่สร้าง Worker Profile และยังไม่ทำเครื่องหมายพร้อม Match";
const START_EKYC_ERROR_TEXT = "เริ่มตรวจ e-KYC ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
const NOT_READY_FOR_MATCH_TEXT = "ยังไม่พร้อม match";
const WORKER_PROFILE_WARNING_TEXT = "อนุมัติ e-KYC แล้วยังไม่สร้าง Worker Profile อัตโนมัติ และยังไม่พร้อม Match";
// P148: what an operator does next once a review is approved. The endpoint
// behind this has existed since Prompt 097 - nothing called it, so the
// lifecycle dead-ended at "approved" behind a permanently disabled button.
const CREATE_PROFILE_BUTTON_TEXT = "สร้าง Worker Profile";
const PROFILE_ACTION_MOVED_TEXT = "กดปุ่ม \"สร้าง Worker Profile\" ได้ที่แผงขั้นตอนถัดไปด้านบน";
const FILL_PROFILE_BUTTON_TEXT = "เติมข้อมูลแรงงานแบบละเอียด";
const CREATE_PROFILE_HINT_TEXT =
  "สร้างแล้วจะเข้าสู่ขั้นเติมข้อมูลละเอียด ก่อนจึงจะทำเครื่องหมายพร้อม Match ได้";
const CREATE_PROFILE_ERROR_TEXT = "สร้าง Worker Profile ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
const PROFILE_CREATED_TEXT =
  "สร้าง Worker Profile แล้ว ขั้นต่อไปคือเติมข้อมูลให้ครบ ก่อนจึงจะทำเครื่องหมายพร้อม Match ได้";
const PROFILE_ONLY_AFTER_APPROVAL_TEXT =
  "จะสร้าง Worker Profile ได้ก็ต่อเมื่อ e-KYC ผ่านการอนุมัติแล้ว";



const STATUS_META = {
  Submitted: { label: "รอตัดสินใจ", badge: "warning" },
  InProgress: { label: "กำลังทำ", badge: "info" },
  NeedsCorrection: { label: "ต้องแก้ไข", badge: "danger" },
  Approved: { label: "ผ่านแล้ว", badge: "success" },
  Rejected: { label: "ไม่ผ่าน", badge: "danger" },
  NotStarted: { label: "ยังไม่เริ่ม", badge: "neutral" },
};

/**
 * P149: the queue's own state badge. The backend resolves one state per row
 * (from the review record when there is one, otherwise from the application's
 * own lifecycle status) and sends its Thai label with it, so the UI never
 * re-derives the lifecycle and cannot disagree with the counts.
 */
/** The states the queue endpoint accepts as a filter, with their Thai labels. */
const QUEUE_STATE_FILTER_OPTIONS = {
  Waiting: { label: "รอ e-KYC" },
  InProgress: { label: "กำลังตรวจ e-KYC" },
  Submitted: { label: "ส่งเอกสารรอตรวจ" },
  NeedsCorrection: { label: "ต้องแก้ไขเอกสาร" },
  Approved: { label: "ผ่าน e-KYC แล้ว" },
  Rejected: { label: "e-KYC ไม่ผ่าน" },
};

const QUEUE_STATE_BADGE = {
  Waiting: "warning",
  InProgress: "info",
  Submitted: "info",
  NeedsCorrection: "warning",
  Approved: "success",
  Rejected: "danger",
};

function queueStateMeta(state, label) {
  return { label: label ?? state ?? "-", badge: QUEUE_STATE_BADGE[state] ?? "neutral" };
}

/**
 * Document presence for a queue row. A row with no review record yet has no
 * review documents at all - what it does have is whatever Quick Register
 * captured, so that is what gets reported rather than a bare zero.
 */
function describeQueueDocuments(item) {
  if (item.reviewRecordExists && (item.submittedDocumentCount > 0 || item.acceptedDocumentCount > 0)) {
    return {
      primary: `ส่งแล้ว ${item.submittedDocumentCount}`,
      sub: `ผ่านแล้ว ${item.acceptedDocumentCount}`,
    };
  }

  return {
    primary: item.hasPassportDocument ? "มีไฟล์หนังสือเดินทาง" : "ยังไม่มีไฟล์เอกสาร",
    sub: item.hasPassportNumber ? "มีเลขหนังสือเดินทาง" : "ยังไม่มีเลขหนังสือเดินทาง",
  };
}

/** Submitted time once a review exists, otherwise when the worker registered. */
function formatQueueTimestamp(item) {
  const value = item.submittedAtUtc ?? item.registeredAtUtc;
  if (!value) {
    return "-";
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "-" : parsed.toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" });
}

/** The narrow-screen equivalent of one queue row. */
function EkycQueueCard({ item, onOpen }) {
  const meta = queueStateMeta(item.queueState, item.queueStateLabel);
  const docs = describeQueueDocuments(item);

  return (
    <button type="button" className="wx-ek-card" onClick={onOpen}>
      <div className="wx-ek-card-head">
        <span className="wx-ek-card-name">{item.fullName || "-"}</span>
        <span className={`wx-badge wx-badge--${meta.badge}`}>{meta.label}</span>
      </div>
      <div className="wx-ek-card-meta">{item.phoneNumber ?? "-"}</div>
      <div className="wx-ek-card-meta">
        {item.nationality ?? "-"} · {item.preferredLanguageCode ?? "-"}
      </div>
      <div className="wx-ek-card-meta">
        {item.agent?.agentCode ?? "-"} · {item.agent?.displayName ?? "-"}
      </div>
      <div className="wx-ek-card-meta">{docs.primary}</div>
      <div className="wx-ek-card-meta">{formatQueueTimestamp(item)}</div>
      <div className="wx-ek-card-action">{item.nextAction?.label ?? "รายละเอียด"}</div>
    </button>
  );
}

function statusMeta(code) {
  return STATUS_META[code] ?? { label: code ?? "-", badge: "neutral" };
}

const RISK_LABELS = { Low: "ต่ำ", Medium: "กลาง", High: "สูง" };
const RISK_VARIANTS = { Low: "low", Medium: "medium", High: "high" };

function riskLabel(level) {
  return RISK_LABELS[level] ?? "-";
}

function riskVariant(level) {
  return RISK_VARIANTS[level] ?? "none";
}

const DOC_TYPE_LABELS = {
  Passport: "Passport",
  FacePhoto: "Face Photo (Selfie)",
  WorkPermitSupportDoc: "Work Permit Support Doc",
};

const DOC_STATUS_META = {
  Accepted: { label: "ผ่านแล้ว", badge: "success" },
  Rejected: { label: "ปฏิเสธ", badge: "danger" },
  NeedsCorrection: { label: "ต้องแก้ไข", badge: "warning" },
  Submitted: { label: "รอตรวจ", badge: "info" },
};

function docStatusMeta(code) {
  return DOC_STATUS_META[code] ?? { label: code ?? "-", badge: "neutral" };
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงคิว e-KYC นี้";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeDetailError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูรายการ e-KYC นี้";
  }
  if (error?.status === 404) {
    return "ไม่พบรายการ e-KYC นี้ อาจถูกลบหรือย้ายแล้ว";
  }
  return "ไม่สามารถโหลดรายละเอียด e-KYC ได้ กรุณาลองใหม่อีกครั้ง";
}

function describeDecisionError(error) {
  if (error?.status === 409) {
    return "สถานะของรายการนี้เปลี่ยนไปแล้ว (เช่น ถูกตัดสินใจไปแล้ว หรือยังไม่ถูกส่งตรวจ) กรุณารีเฟรชแล้วลองใหม่อีกครั้ง";
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์บันทึกการตัดสินใจสำหรับรายการนี้";
  }
  if (error?.status === 400) {
    return "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบการตัดสินใจและเหตุผลอีกครั้ง";
  }
  return "ไม่สามารถบันทึกการตัดสินใจได้ กรุณาลองใหม่อีกครั้ง";
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

// Shorter than formatDateTimeTh (no year, no "ICT" suffix) - used only for
// the drawer timeline, where fitting all timeline steps plus every other
// Details-tab section on one screen without an internal drawer scroll
// (matching the W45 reference's viewport-fit fix) matters more than the
// full date precision the document list's own "ตรวจเมื่อ" field still shows.
function formatDateTimeThShort(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart}`;
}

function formatAgeHours(ageHours) {
  if (ageHours === null || ageHours === undefined) {
    return "-";
  }
  if (ageHours < 1) {
    return "เมื่อครู่";
  }
  if (ageHours < 24) {
    return `${ageHours} ชม.ที่แล้ว`;
  }
  return `${Math.floor(ageHours / 24)} วันที่แล้ว`;
}

function describeDocumentSummary(documentSummary) {
  if (!documentSummary) {
    return { primary: "-", sub: null };
  }
  const requiredCount = documentSummary.requiredCount ?? 0;
  const submittedCount = documentSummary.submittedCount ?? 0;
  const needsCorrectionCount = documentSummary.needsCorrectionCount ?? 0;
  const primary =
    requiredCount > 0 && submittedCount >= requiredCount ? `${submittedCount}/${requiredCount} ครบ` : `${submittedCount}/${requiredCount} ส่งแล้ว`;
  return { primary, sub: `${needsCorrectionCount} ต้องแก้ไข` };
}

function isOverSla(item) {
  return item.reviewStatus === "Submitted" && typeof item.ageHours === "number" && item.ageHours > SLA_HOURS_THRESHOLD;
}

/** "YYYY-MM-DD" (native date input value) -> UTC day-boundary ISO string. */
function dateInputToIsoBoundary(value, boundary) {
  if (!value) {
    return "";
  }
  return boundary === "end" ? `${value}T23:59:59.999Z` : `${value}T00:00:00.000Z`;
}

/**
 * /backoffice/worker-intake/ekyc-review - the e-KYC Review Queue (Prompt
 * 096B, built from the W45 reference). SuperAdmin backoffice screen for
 * reviewing e-KYC document submissions from workers who already completed
 * Quick Register (Prompt 093/094B). Read + one write action (the decision
 * endpoint) over the Prompt 095/095R API: this screen never creates a
 * WmxWorkers row, never marks anything ready for match, and never touches
 * matching - stage.readyForMatch and nextActions.canCreateWorkerProfile /
 * canMarkReadyForMatch are always rendered as disabled/reserved regardless
 * of what any response value says.
 *
 * Filtering/pagination is real server-side (mirrors
 * useWorkerApplicationsList.js), and only ever forwards the P096-whitelisted
 * query params (see ekycReviewsApi.js) - never an unfiltered pass-through of
 * whatever the UI happens to hold in state.
 */
export default function EkycReviewQueuePage() {
  const { data: identity } = useBackofficeIdentity();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [riskFilter, setRiskFilter] = useState("");
  const [nationalityFilter, setNationalityFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [page, setPage] = useState(1);
  // Row click opens the ONE shared worker record; the review surface stays
  // behind the row's own action button.
  const [workerRecordApplicationId, setWorkerRecordApplicationId] = useState(null);

  const [selectedReviewId, setSelectedReviewId] = useState(null);
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);

  const [activeTab, setActiveTab] = useState("details");
  const [decisionChoice, setDecisionChoice] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [decisionValidationError, setDecisionValidationError] = useState(null);
  const [decisionSubmit, setDecisionSubmit] = useState({ status: "idle", error: null });
  // P148: creating the Worker Profile from an approved review - the step
  // that used to be a permanently disabled button.
  const [createProfileState, setCreateProfileState] = useState({ status: "idle", message: null, workerProfileId: null });
  // P149-R: starting (or resuming) the real e-KYC review for an application.
  const [startState, setStartState] = useState({ status: "idle", message: null });

  // P149-R2: the per-item e-KYC check. Reset whenever a different review is
  // opened - carrying one worker's judgements onto another would be the worst
  // possible bug on this screen.
  const [checkResults, setCheckResults] = useState({});
  useEffect(() => {
    setCheckResults({});
  }, [selectedReviewId]);

  function handleCheckChange(key, value) {
    setCheckResults((current) => ({ ...current, [key]: value }));
  }

  // Debounce free-text search before it ever becomes part of the fetch
  // filters - typing does not fire one request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Any real filter change (not a page change) returns to page 1.
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, riskFilter, nationalityFilter, agentFilter, dateFromFilter, dateToFilter]);

  // Opening a different row (or closing the drawer) always starts the
  // decision form fresh - re-clicking the already-open row is a no-op
  // (selectedReviewId does not change), so an in-progress reason draft is
  // never silently wiped by Refresh or by re-clicking the same row.
  useEffect(() => {
    setActiveTab("details");
    setDecisionChoice(null);
    setReasonText("");
    setDecisionValidationError(null);
    setDecisionSubmit({ status: "idle", error: null });
  }, [selectedReviewId]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: EKYC_REVIEWS_DEFAULT_PAGE_SIZE,
      status: statusFilter,
      riskLevel: riskFilter,
      nationality: nationalityFilter,
      agentId: agentFilter,
      dateFromUtc: dateInputToIsoBoundary(dateFromFilter, "start"),
      dateToUtc: dateInputToIsoBoundary(dateToFilter, "end"),
      search,
    }),
    [page, statusFilter, riskFilter, nationalityFilter, agentFilter, dateFromFilter, dateToFilter, search]
  );

  // P149: the screen is driven by the WORK queue, not by review records. The
  // old source listed dbo.WmxEkycReviews rows, so a Quick Registered worker
  // nobody had started a review for never appeared - which is why this page
  // rendered zero rows against thousands of real applications.
  const queueFilters = useMemo(
    () => ({
      page,
      pageSize: EKYC_QUEUE_PAGE_SIZE,
      state: statusFilter,
      nationality: nationalityFilter,
      agentId: agentFilter,
      search,
    }),
    [page, statusFilter, nationalityFilter, agentFilter, search],
  );

  const {
    status: listStatus,
    data: listData,
    error: listError,
    refetch: refetchList,
  } = useEkycQueue(queueFilters, detailReloadToken);

  // The row the drawer was opened from. selectedReviewId now holds a worker
  // APPLICATION id, so anything keyed by the review (the decision endpoint, the
  // detail fetch) has to resolve through this row's own ekycReviewId.
  const selectedQueueRow =
    (listData?.items ?? []).find((row) => row.workerApplicationId === selectedReviewId) ?? null;

  // Detail is fetched only when a row is opened, and only refetched for the
  // same open row when Refresh is pressed or a decision is saved
  // (detailReloadToken) - never on an interval, never speculatively.
  useEffect(() => {
    if (!selectedReviewId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forId === selectedReviewId ? previous.data : null,
      error: null,
      forId: selectedReviewId,
    }));

    // P149: rows are addressed by worker application. A row with no review
    // record has no detail endpoint to call - the drawer renders the queue row
    // it already has and offers "start e-KYC", which is the call that creates
    // the record.
    const queueRow = (listData?.items ?? []).find((row) => row.workerApplicationId === selectedReviewId);
    if (queueRow && !queueRow.reviewRecordExists) {
      setDetailState({ status: "ready", data: null, error: null, forId: selectedReviewId });
      return () => {
        cancelled = true;
      };
    }

    const reviewId = queueRow?.ekycReviewId ?? selectedReviewId;

    getEkycReview(reviewId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedReviewId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedReviewId });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedReviewId
  }, [selectedReviewId, detailReloadToken, listData]);

  function openDrawer(reviewId) {
    setSelectedReviewId(reviewId);
  }

  function closeDrawer() {
    setSelectedReviewId(null);
  }

  function handleRefresh() {
    refetchList();
    if (selectedReviewId) {
      setDetailReloadToken((token) => token + 1);
    }
  }

  function handleSelectDecisionChoice(choice) {
    setDecisionChoice(choice);
    setDecisionValidationError(null);
  }

  /**
   * P149-R: creates or re-opens the real e-KYC review for the selected worker
   * application.
   *
   * POST /api/backoffice/worker-applications/{applicationId}/ekyc/start has
   * existed since Prompt 095 and is idempotent by application id:
   * EkycReviewService.StartEkycAsync resumes an InProgress/NeedsCorrection
   * review instead of inserting a second one, and refuses (409, with no write
   * at all) a review that is already Submitted/Approved/Rejected. So clicking
   * twice can never produce a duplicate - the guarantee lives in the service,
   * not in this handler.
   *
   * On success the queue and the drawer are both re-read from the server, so
   * the new state and the KPI counts come from real data rather than a local
   * optimistic patch.
   */
  async function handleStartEkyc() {
    if (!selectedReviewId) {
      return;
    }
    setStartState({ status: "submitting", message: null });
    try {
      await startEkyc(selectedReviewId);
      setStartState({ status: "idle", message: null });
      setDetailReloadToken((token) => token + 1);
    } catch (error) {
      setStartState({
        status: "error",
        // 409 carries the service's own Thai explanation (already submitted,
        // already decided) - show it rather than a generic failure.
        message: error?.body?.message ?? START_EKYC_ERROR_TEXT,
      });
    }
  }

  async function handleCreateWorkerProfile() {
    const ekycReviewId = selectedQueueRow?.ekycReviewId ?? null;
    if (!ekycReviewId) {
      setCreateProfileState({
        status: "error",
        message: CREATE_PROFILE_NO_REVIEW_TEXT,
        workerProfileId: null,
      });
      return;
    }
    setCreateProfileState({ status: "submitting", message: null, workerProfileId: null });
    try {
      const created = await createWorkerProfileFromEkycReview(ekycReviewId);
      setCreateProfileState({
        status: "success",
        message: null,
        workerProfileId: created?.workerId ?? null,
      });
      // Re-reads the review so the drawer reflects the new profile rather
      // than relying on the local flag alone.
      setDetailReloadToken((token) => token + 1);
    } catch (error) {
      setCreateProfileState({
        status: "error",
        // 409 means the backend refused on its own rules (already created,
        // or the review is not approved) - show what it said.
        message: error?.body?.message ?? CREATE_PROFILE_ERROR_TEXT,
        workerProfileId: null,
      });
    }
  }

  function handleProfileSaved() {
    setDetailReloadToken((token) => token + 1);
  }

  async function handleSubmitDecision(event) {
    event.preventDefault();
    if (!decisionChoice) {
      setDecisionValidationError("กรุณาเลือกการตัดสินใจก่อนบันทึก");
      return;
    }
    if (!reasonText.trim()) {
      setDecisionValidationError("กรุณาระบุเหตุผลก่อนบันทึกการตัดสินใจ");
      return;
    }
    setDecisionValidationError(null);
    setDecisionSubmit({ status: "submitting", error: null });
    try {
      const reviewId = selectedQueueRow?.ekycReviewId ?? selectedReviewId;
      await submitEkycDecision(reviewId, {
        decision: decisionChoice,
        reason: composeChecklistReason(checkResults, reasonText),
      });
      setDecisionSubmit({ status: "success", error: null });
      setDecisionChoice(null);
      setReasonText("");
      // A successful decision changes both this row's status/nextActions and
      // the KPI summary - refresh both instead of only patching local state,
      // so the table/KPI strip and the drawer always reflect what the
      // backend actually persisted.
      refetchList();
      setDetailReloadToken((token) => token + 1);
    } catch (error) {
      setDecisionSubmit({ status: "error", error });
    }
  }

  const items = listData?.items ?? [];
  const summary = listData?.summary ?? null;
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / EKYC_QUEUE_PAGE_SIZE));
  const hasActiveFilters = Boolean(
    search || statusFilter || riskFilter || nationalityFilter || agentFilter || dateFromFilter || dateToFilter
  );

  // Derived-only KPIs (see SLA_HOURS_THRESHOLD comment above) - computed
  // from whatever page of rows is currently loaded, never claimed as a
  // platform-wide total.
  // P149: was counted from the current page only, which disagreed with every
  // other card. The queue summary now reports this system-wide from the same
  // query the rows come from.
  const inProgressOnPage = summary?.inProgressCount ?? 0;
  const overSlaOnPage = items.filter(isOverSla).length;

  const isInitialLoading = listStatus === "loading" && listData === null;
  const isInitialError = listStatus === "error" && listData === null;

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="table" label="กำลังโหลดคิวตรวจ e-KYC..." />
    </AppShell>
    );
  }

  if (isInitialError) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดคิวตรวจ e-KYC ได้</h1>
          <p className="wx-state-body">{describeListError(listError)}</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-ek-page">
        <div className="wx-ek-toolbar">
          <label className="wx-ek-search">
            <span className="wx-visually-hidden">ค้นหาชื่อแรงงาน เบอร์โทร รหัสใบสมัคร หรือ Agent</span>
            <input
              type="search"
              placeholder="ค้นหาชื่อแรงงาน เบอร์โทร รหัสใบสมัคร หรือ Agent"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <div className="wx-ek-filters">
            <label className="wx-ek-filter">
              <span>สถานะ</span>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {Object.entries(QUEUE_STATE_FILTER_OPTIONS).map(([code, meta]) => (
                  <option key={code} value={code}>
                    {meta.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-ek-filter">
              <span>ระดับความเสี่ยง</span>
              <select value={riskFilter} onChange={(event) => setRiskFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {Object.entries(RISK_LABELS).map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-ek-filter">
              <span>สัญชาติ</span>
              <select value={nationalityFilter} onChange={(event) => setNationalityFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {(summary?.byNationality ?? []).map((entry) => (
                  <option key={entry.nationality} value={entry.nationality}>
                    {entry.nationality} ({entry.count})
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-ek-filter">
              <span>Agent</span>
              <select value={agentFilter} onChange={(event) => setAgentFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {(summary?.byAgent ?? []).map((entry) => (
                  <option key={entry.agentId} value={entry.agentId}>
                    {entry.agentCode} · {entry.displayName}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-ek-filter wx-ek-filter--date">
              <span>ช่วงวันที่</span>
              <span className="wx-ek-date-range">
                <input type="date" value={dateFromFilter} onChange={(event) => setDateFromFilter(event.target.value)} />
                <span aria-hidden="true">-</span>
                <input type="date" value={dateToFilter} onChange={(event) => setDateToFilter(event.target.value)} />
              </span>
            </label>
          </div>

          <div className="wx-ek-toolbar-actions">
            {/* wx-ek-refresh-button is also listed in DetailDrawer's
                selectableSelector below - without it, clicking Refresh while
                a row is open is an "outside click" as far as the drawer's
                own close-on-outside-click listener is concerned, which
                would close the drawer instead of refreshing its content. */}
            <button type="button" className="wx-button wx-button--ghost wx-ek-refresh-button" onClick={handleRefresh}>
              Refresh
            </button>
          </div>
        </div>

        <header className="wx-ek-head">
          <div className="wx-ek-title">
            <h1>e-KYC Review</h1>
            <p>ตรวจเอกสารและยืนยันตัวตนหลัง Quick Register ก่อนแรงงานจะไปขั้นตอนสร้าง Worker Profile และ Match งานได้</p>
          </div>
          {identity?.primaryRole && (
            <span className="wx-ek-scope-pill">
              Scope <b>{identity.primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-ek-metrics">
          <MetricCard label="รอ e-KYC" value={summary?.waitingCount} note="Quick Register แล้ว ยังไม่เริ่มตรวจ" variant="amber" />
          <MetricCard
            label="กำลังทำ e-KYC"
            value={inProgressOnPage}
            note="เริ่มตรวจแล้ว รอผลตัดสิน"
            variant="derived"
          />
          <MetricCard label="ต้องแก้ไข" value={summary?.needsCorrectionCount} note="ส่งเอกสารกลับไปแก้" variant="amber" />
          <MetricCard label="ผ่านแล้ว" value={summary?.approvedCount} note="อนุมัติแล้ว ยังไม่ Ready for Match" variant="green" />
          <MetricCard label="ไม่ผ่าน" value={summary?.rejectedCount} note="ปฏิเสธแล้ว" variant="red" />
          <MetricCard
            label={`เกิน SLA (>${SLA_HOURS_THRESHOLD} ชม.)`}
            value={overSlaOnPage}
            note={`จากรายการหน้านี้ · รอนานสุดทั้งระบบ ${summary?.oldestWaitingHours ?? "-"} ชม.`}
            variant="derived"
          />
        </div>

        <section className="wx-panel wx-ek-list-panel">
          <div className="wx-ek-list-head">
            <h2 className="wx-panel-title">คิวตรวจ e-KYC</h2>
            <span className="wx-ek-list-count">
              {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
          </div>

          {listStatus === "error" && listData !== null && (
            <p className="wx-ek-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {items.length === 0 ? (
            <p className="wx-empty-list">
              {hasActiveFilters ? "ไม่พบรายการที่ตรงกับตัวกรองหรือคำค้นหา" : "ยังไม่มีรายการ e-KYC ในระบบ"}
            </p>
          ) : (
            <>
              <div className="wx-ek-table-wrap">
                <table className="wx-ek-table">
                  <colgroup>
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "6%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "13%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Worker</th>
                      <th>Phone</th>
                      <th>Nationality</th>
                      <th>Agent</th>
                      <th>Line</th>
                      <th>e-KYC Status</th>
                      <th>Risk</th>
                      <th>Documents</th>
                      <th>Submitted/Started</th>
                      <th aria-hidden="true">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      // P149: queueState is the backend's single resolved
                      // state, so the row, the counts and the drawer can never
                      // disagree about what this person is waiting on.
                      const meta = queueStateMeta(item.queueState, item.queueStateLabel);
                      const docs = describeQueueDocuments(item);
                      const overSla = false;
                      const fullName = item.fullName || "-";
                      return (
                        <tr
                          key={item.workerApplicationId}
                          className="wx-ek-row"
                          onClick={() => setWorkerRecordApplicationId(item.workerApplicationId)}
                        >
                          <td>
                            <span className="wx-ek-cell-strong">{fullName}</span>
                            <span className="wx-ek-cell-sub">{item.workerApplicationId}</span>
                          </td>
                          <td>
                            <span className="wx-ek-cell-strong">{item.phoneNumber ?? "-"}</span>
                          </td>
                          <td>
                            <span className="wx-ek-cell-strong">{item.nationality ?? "-"}</span>
                            <span className="wx-ek-cell-sub">{item.preferredLanguageCode ?? "-"}</span>
                          </td>
                          <td>
                            <span className="wx-ek-cell-strong">{item.agent?.agentCode ?? "-"}</span>
                            <span className="wx-ek-cell-sub">{item.agent?.displayName ?? "-"}</span>
                          </td>
                          <td>
                            <span className="wx-ek-cell-strong">{item.line?.superAgentName ?? "-"}</span>
                            <span className="wx-ek-cell-sub">{item.line?.plcName ?? "-"}</span>
                          </td>
                          <td>
                            <span className={`wx-badge wx-badge--${meta.badge}`}>{meta.label}</span>
                          </td>
                          <td>
                            {/* Risk is only recorded on a real review row. */}
                            <span className={`wx-ek-risk wx-ek-risk--${riskVariant(item.riskLevel)}`}>
                              {item.reviewRecordExists ? riskLabel(item.riskLevel) : "-"}
                            </span>
                          </td>
                          <td>
                            <span className="wx-ek-cell-strong">{docs.primary}</span>
                            <span className="wx-ek-cell-sub">{docs.sub}</span>
                          </td>
                          <td>
                            <span className="wx-ek-cell-strong">{formatQueueTimestamp(item)}</span>
                            {overSla && <span className="wx-ek-cell-sla">เกิน SLA</span>}
                          </td>
                          <td>
                            {/* The backend resolves the single next step, so a
                                row never offers something the API would refuse. */}
                            <button
                              type="button"
                              className="wx-ek-row-action"
                              onClick={(event) => {
                                event.stopPropagation();
                                openDrawer(item.workerApplicationId);
                              }}
                            >
                              {item.nextAction?.label ?? "รายละเอียด"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="wx-ek-cards">
                {items.map((item) => (
                  <EkycQueueCard
                    key={item.workerApplicationId}
                    item={item}
                    onOpen={() => openDrawer(item.workerApplicationId)}
                  />
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
      </div>

      <DetailDrawer
        open={selectedReviewId !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-ek-row, .wx-ek-card, .wx-ek-refresh-button"
      >
        <EkycReviewDrawerContent
          state={detailState}
          titleId={DRAWER_TITLE_ID}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          decisionChoice={decisionChoice}
          onSelectDecisionChoice={handleSelectDecisionChoice}
          reasonText={reasonText}
          onReasonChange={setReasonText}
          decisionValidationError={decisionValidationError}
          decisionSubmit={decisionSubmit}
          onSubmitDecision={handleSubmitDecision}
          createProfileState={createProfileState}
          onCreateWorkerProfile={handleCreateWorkerProfile}
          onProfileSaved={handleProfileSaved}
          queueRow={selectedQueueRow}
          checkResults={checkResults}
          onCheckChange={handleCheckChange}
          onStartEkyc={handleStartEkyc}
          startState={startState}
        />
      </DetailDrawer>
      
        <WorkerRecordDrawer
        open={workerRecordApplicationId !== null}
        applicationId={workerRecordApplicationId}
        onClose={() => setWorkerRecordApplicationId(null)}
        selectableSelector="tr.wx-ek-row, .wx-ek-card"
      />
    </AppShell>
  );
}

function MetricCard({ label, value, note, variant }) {
  const variantClass = variant ? ` wx-ek-metric--${variant}` : "";
  return (
    <div className={`wx-ek-metric${variantClass}`}>
      <span className="wx-ek-metric-label">{label}</span>
      <strong className="wx-ek-metric-value">{value ?? "-"}</strong>
      {note && <span className="wx-ek-metric-note">{note}</span>}
    </div>
  );
}

function EkycReviewCard({ item, onOpen }) {
  const meta = statusMeta(item.reviewStatus);
  const docs = describeDocumentSummary(item.documentSummary);
  const fullName = item.worker?.fullNameNative || item.worker?.fullNameEnglish || "-";
  return (
    <div className="wx-ek-card" onClick={onOpen}>
      <div className="wx-ek-card-top">
        <div>
          <strong>{fullName}</strong>
          <span>
            {item.applicationId} · {item.worker?.nationality ?? "-"}
          </span>
        </div>
        <span className={`wx-badge wx-badge--${meta.badge}`}>{meta.label}</span>
      </div>
      <div className="wx-ek-card-grid">
        <div>
          <label>เบอร์โทร</label>
          <strong>{item.worker?.phoneNumber ?? "-"}</strong>
        </div>
        <div>
          <label>Agent</label>
          <strong>
            {item.agent?.agentCode ?? "-"} · {item.agent?.displayName ?? "-"}
          </strong>
        </div>
        <div>
          <label>สายงาน</label>
          <strong>
            {item.line?.superAgentName ?? "-"} · {item.line?.plcName ?? "-"}
          </strong>
        </div>
        <div>
          <label>Risk</label>
          <strong className={`wx-ek-risk wx-ek-risk--${riskVariant(item.riskLevel)}`}>{riskLabel(item.riskLevel)}</strong>
        </div>
        <div>
          <label>เอกสาร</label>
          <strong>
            {docs.primary} · {docs.sub}
          </strong>
        </div>
        <div>
          <label>ส่ง/เริ่มเมื่อ</label>
          <strong>{formatAgeHours(item.ageHours)}</strong>
        </div>
      </div>
      <button
        type="button"
        className="wx-ek-row-action wx-ek-row-action--full"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        รายละเอียด
      </button>
    </div>
  );
}

/**
 * P149: the drawer for a queue row that has no e-KYC review record yet.
 *
 * This is the common case straight after Quick Register, and it used to be
 * unreachable entirely - the row never appeared in the list. Everything shown
 * here is real application data the queue already returned; nothing is invented
 * to stand in for the missing review.
 */

/**
 * P149-R: the panel that answers "what do I do with this person" the moment the
 * drawer opens.
 *
 * Before this, the drawer led with identity data and buried the decision in a
 * second tab, while the only visible primary control was a permanently disabled
 * "เริ่ม e-KYC ใหม่". A reviewer had to already know the lifecycle to get
 * anywhere.
 *
 * One rule drives the whole component: there is never a disabled primary
 * button. A state that cannot be acted on renders as a status panel explaining
 * who is blocked and on what - a greyed button says "you may do this" and then
 * refuses, which is worse than saying nothing.
 */

/**
 * P149-R2: the actual e-KYC check.
 *
 * Until now this screen recorded a verdict without recording a verification: a
 * reviewer pressed อนุมัติ and typed free text. Nothing captured what was
 * compared against what. This is the check itself - each item is judged
 * ผ่าน/ไม่ผ่าน with a short note, and every item must pass before อนุมัติ is
 * available.
 *
 * The result is not a separate stored object. There is no backend column for a
 * per-item checklist, and inventing one client-side would produce a record the
 * server never saw. Instead the outcome is composed into the decision reason
 * that the existing endpoint already persists, so the audit trail carries the
 * evidence rather than a bare verdict.
 */
const EKYC_CHECKLIST_ITEMS = [
  { key: "faceMatch", label: "ใบหน้าตรงกับภาพในหนังสือเดินทาง" },
  { key: "documentLegible", label: "ภาพเอกสารชัดเจน อ่านข้อมูลได้ครบ" },
  { key: "notExpired", label: "หนังสือเดินทางยังไม่หมดอายุ" },
  { key: "nameMatch", label: "ชื่อ-นามสกุล ตรงกับเอกสาร" },
  { key: "profileMatch", label: "วันเกิดและสัญชาติ ตรงกับเอกสาร" },
  { key: "noTampering", label: "ไม่พบร่องรอยการแก้ไขหรือปลอมแปลง" },
];

const CHECK_PASS = "pass";
const CHECK_FAIL = "fail";

function checklistOutcome(results) {
  const judged = EKYC_CHECKLIST_ITEMS.filter((item) => results[item.key]?.verdict);
  const failed = EKYC_CHECKLIST_ITEMS.filter((item) => results[item.key]?.verdict === CHECK_FAIL);
  return {
    judgedCount: judged.length,
    totalCount: EKYC_CHECKLIST_ITEMS.length,
    failed,
    allJudged: judged.length === EKYC_CHECKLIST_ITEMS.length,
    allPassed: judged.length === EKYC_CHECKLIST_ITEMS.length && failed.length === 0,
  };
}

/**
 * Turns the checklist into the line-per-item text stored on the decision, so
 * anyone reading the record later sees exactly what was checked.
 */
export function composeChecklistReason(results, closingNote) {
  const lines = EKYC_CHECKLIST_ITEMS.map((item) => {
    const entry = results[item.key] ?? {};
    const mark = entry.verdict === CHECK_PASS ? "ผ่าน" : entry.verdict === CHECK_FAIL ? "ไม่ผ่าน" : "ยังไม่ตรวจ";
    const note = entry.note?.trim();
    return note ? `${item.label}: ${mark} (${note})` : `${item.label}: ${mark}`;
  });
  const closing = closingNote?.trim();
  return closing ? `${lines.join("\n")}\n\nหมายเหตุ: ${closing}` : lines.join("\n");
}

function EkycChecklist({ results, onChange, disabled }) {
  const outcome = checklistOutcome(results);

  return (
    <section className="wx-ek-dsec">
      <h3>ตรวจ e-KYC</h3>
      <p className="wx-ek-checklist-lead">
        ตรวจให้ครบทุกข้อ ต้องผ่านทั้งหมดจึงจะอนุมัติได้ ({outcome.judgedCount}/{outcome.totalCount})
      </p>

      <ul className="wx-ek-checklist">
        {EKYC_CHECKLIST_ITEMS.map((item) => {
          const entry = results[item.key] ?? {};
          return (
            <li key={item.key} className="wx-ek-check">
              <p className="wx-ek-check-label">{item.label}</p>
              <div className="wx-ek-check-choices" role="group" aria-label={item.label}>
                <button
                  type="button"
                  className={`wx-ek-check-btn wx-ek-check-btn--pass${entry.verdict === CHECK_PASS ? " wx-ek-check-btn--on" : ""}`}
                  aria-pressed={entry.verdict === CHECK_PASS}
                  disabled={disabled}
                  onClick={() => onChange(item.key, { ...entry, verdict: CHECK_PASS })}
                >
                  ผ่าน
                </button>
                <button
                  type="button"
                  className={`wx-ek-check-btn wx-ek-check-btn--fail${entry.verdict === CHECK_FAIL ? " wx-ek-check-btn--on" : ""}`}
                  aria-pressed={entry.verdict === CHECK_FAIL}
                  disabled={disabled}
                  onClick={() => onChange(item.key, { ...entry, verdict: CHECK_FAIL })}
                >
                  ไม่ผ่าน
                </button>
              </div>
              {/* Short by design - a reviewer notes what they saw, not an essay. */}
              <input
                type="text"
                className="wx-ek-check-note"
                maxLength={120}
                disabled={disabled}
                placeholder="รายละเอียดสั้น ๆ (ถ้ามี)"
                aria-label={`รายละเอียด: ${item.label}`}
                value={entry.note ?? ""}
                onChange={(event) => onChange(item.key, { ...entry, note: event.target.value })}
              />
            </li>
          );
        })}
      </ul>

      {outcome.failed.length > 0 && (
        <p className="wx-ek-form-message">
          มีข้อที่ไม่ผ่าน {outcome.failed.length} ข้อ จึงอนุมัติไม่ได้ ให้เลือกส่งกลับไปแก้ไข หรือไม่อนุมัติ
        </p>
      )}
    </section>
  );
}

function DrawerNextActionPanel({
  state,
  approvedWorkerProfileId,
  documentBlocker,
  onStartEkyc,
  canAct = true,
  blockedReason,
  onCreateWorkerProfile,
  onOpenWorkerProfile,
  startState,
  createProfileState,
  reason,
}) {
  const starting = startState?.status === "submitting";
  const creating = createProfileState?.status === "submitting";

  // --- Terminal / blocked states: a panel, never a dead control ---

  if (state === EKYC_UI_STATES.Rejected) {
    return (
      <section className="wx-ek-next wx-ek-next--closed">
        <p className="wx-ek-next-kicker">สถานะสุดท้าย</p>
        <h3 className="wx-ek-next-title">e-KYC ไม่ผ่าน</h3>
        <p className="wx-ek-next-body">{reason || "ไม่มีเหตุผลบันทึกไว้ในระบบ"}</p>
        <p className="wx-ek-next-owner">ขั้นตอน e-KYC ของแรงงานคนนี้จบแล้ว ไม่มีการดำเนินการต่อในหน้านี้</p>
      </section>
    );
  }

  if (state === EKYC_UI_STATES.NeedsCorrection) {
    return (
      <section className="wx-ek-next wx-ek-next--warn">
        <p className="wx-ek-next-kicker">รอการแก้ไข</p>
        <h3 className="wx-ek-next-title">ต้องแก้ไขเอกสารก่อนตรวจต่อ</h3>
        <p className="wx-ek-next-body">{reason || "ยังไม่มีรายละเอียดการแก้ไขบันทึกไว้"}</p>
        {/* Naming who is blocked is the point - "ต้องแก้ไข" alone leaves a
            reviewer waiting on a queue nobody is working. */}
        <p className="wx-ek-next-owner">{NEEDS_CORRECTION_OWNER_TEXT}</p>
      </section>
    );
  }

  // --- Actionable states ---

  if (state === EKYC_UI_STATES.Waiting) {
    // Permission is the backend's answer, not this component's guess. When it
    // says no, this is a status panel - never a greyed button that implies the
    // action is available to whoever is looking.
    if (!canAct) {
      return (
        <section className="wx-ek-next wx-ek-next--closed">
          <p className="wx-ek-next-kicker">ขั้นตอนถัดไป</p>
          <h3 className="wx-ek-next-title">รอทีมส่วนกลางเริ่มตรวจ e-KYC</h3>
          <p className="wx-ek-next-owner">{blockedReason}</p>
        </section>
      );
    }

    return (
      <section className="wx-ek-next">
        <p className="wx-ek-next-kicker">ขั้นตอนถัดไป</p>
        <h3 className="wx-ek-next-title">ยังไม่ได้เริ่มตรวจ e-KYC</h3>
        <p className="wx-ek-next-body">{START_EKYC_HINT_TEXT}</p>
        <button type="button" className="wx-button wx-button--primary" disabled={starting} onClick={onStartEkyc}>
          {starting ? "กำลังสร้างคิว e-KYC..." : START_EKYC_BUTTON_TEXT}
        </button>
        {startState?.status === "error" && (
          <p className="wx-ek-form-message" role="alert">
            {startState.message ?? START_EKYC_ERROR_TEXT}
          </p>
        )}
      </section>
    );
  }

  if (state === EKYC_UI_STATES.InProgress) {
    // P157-R: the old "ตัดสินใจ e-KYC" button was removed. It only set a
    // vestigial tab that never gated anything - the decision form already
    // renders in this same scroll just below - so it was a dead control that
    // read as a second, confusing action. This panel is now a plain hint that
    // points down to the real decision form.
    return (
      <section className="wx-ek-next">
        <p className="wx-ek-next-kicker">ขั้นตอนถัดไป</p>
        <h3 className="wx-ek-next-title">กำลังตรวจ e-KYC - รอการตัดสินใจ</h3>
        <p className="wx-ek-next-body">
          ตรวจเอกสารและข้อมูลด้านล่าง แล้วบันทึกผลการตรวจ (อนุมัติ / ให้แก้ไข / ไม่อนุมัติ) ที่ด้านล่างของหน้านี้
        </p>
        {documentBlocker && <p className="wx-ek-next-owner">{documentBlocker.summary}</p>}
      </section>
    );
  }

  if (state === EKYC_UI_STATES.Approved) {
    if (approvedWorkerProfileId) {
      return (
        <section className="wx-ek-next wx-ek-next--done">
          <p className="wx-ek-next-kicker">ขั้นตอนถัดไป</p>
          <h3 className="wx-ek-next-title">สร้าง Worker Profile แล้ว</h3>
          <p className="wx-ek-next-body">{PROFILE_NEXT_STEP_TEXT}</p>
          {/* Secondary only - the primary work stays in this drawer. */}
          <button type="button" className="wx-button wx-button--ghost" onClick={onOpenWorkerProfile}>
            {OPEN_PROFILE_TEXT}
          </button>
        </section>
      );
    }

    return (
      <section className="wx-ek-next wx-ek-next--done">
        <p className="wx-ek-next-kicker">ขั้นตอนถัดไป</p>
        <h3 className="wx-ek-next-title">ผ่าน e-KYC แล้ว - รอสร้าง Worker Profile</h3>
        <p className="wx-ek-next-body">{CREATE_PROFILE_HINT_TEXT}</p>
        <button type="button" className="wx-button wx-button--primary" disabled={creating} onClick={onCreateWorkerProfile}>
          {creating ? "กำลังสร้าง..." : CREATE_PROFILE_BUTTON_TEXT}
        </button>
        {createProfileState?.status === "error" && (
          <p className="wx-ek-form-message" role="alert">
            {createProfileState.message ?? CREATE_PROFILE_ERROR_TEXT}
          </p>
        )}
      </section>
    );
  }

  return null;
}

/**
 * P149-R: what is missing before this review can be approved, and who has to
 * supply it.
 *
 * A bare "เอกสาร 0" told a reviewer nothing actionable. This names the gap and
 * the owner, and it is the same list the approve control is gated on - so the
 * reason approval is unavailable is always visible rather than inferred.
 */
function resolveDocumentBlocker(detail, queueRow) {
  const documents = detail?.documents ?? [];
  const hasReviewDocument = documents.length > 0;
  const hasPassportFile = queueRow?.hasPassportDocument ?? false;
  const hasPassportNumber = queueRow?.hasPassportNumber ?? false;

  const missing = [];
  if (!hasPassportNumber) {
    missing.push({ key: "passportNumber", label: "เลขหนังสือเดินทาง", owner: DOC_OWNER_BACKOFFICE });
  }
  if (!hasPassportFile && !hasReviewDocument) {
    missing.push({ key: "passportDocument", label: "ไฟล์ภาพหนังสือเดินทาง", owner: DOC_OWNER_WORKER_AGENT });
  }

  if (missing.length === 0) {
    return null;
  }

  return {
    missing,
    summary: `ยังขาด ${missing.map((item) => item.label).join(", ")} จึงยังอนุมัติไม่ได้`,
  };
}

function EkycQueueOnlyDrawer({ titleId, row, onStartEkyc, startState }) {
  if (!row) {
    return (
      <div className="wx-ek-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่พบข้อมูลรายการนี้
        </h2>
      </div>
    );
  }

  const action = row.nextAction ?? {};
  const starting = startState?.status === "submitting";

  return (
    <div className="wx-ek-drawer">
      <span className="wx-ek-drawer-kicker">EKYC QUEUE</span>
      <h2 id={titleId} className="wx-ek-drawer-title">
        {row.fullName || "-"}
      </h2>
      <p className="wx-ek-drawer-sub">{row.workerApplicationId}</p>

      <DrawerNextActionPanel
        state={EKYC_UI_STATES.Waiting}
        documentBlocker={null}
        onStartEkyc={onStartEkyc}
        canAct={action.canAct !== false}
        blockedReason={action.blockedReason}
        startState={startState}
      />

      <div className="wx-ek-drawer-status">
        <span className="wx-badge wx-badge--warning">{row.queueStateLabel}</span>
        <span className="wx-ek-notice">ยังไม่มีเรคคอร์ด e-KYC สำหรับแรงงานคนนี้</span>
      </div>

      <section className="wx-ek-dsec">
        <h3>ข้อมูลผู้สมัคร</h3>
        <div className="wx-ek-kv">
          <div className="wx-ek-kv-row">
            <span>เบอร์โทร</span>
            <strong>{row.phoneNumber ?? "-"}</strong>
          </div>
          <div className="wx-ek-kv-row">
            <span>สัญชาติ</span>
            <strong>{row.nationality ?? "-"}</strong>
          </div>
          <div className="wx-ek-kv-row">
            <span>ภาษา</span>
            <strong>{row.preferredLanguageCode ?? "-"}</strong>
          </div>
          <div className="wx-ek-kv-row">
            <span>สถานะใบสมัคร</span>
            <strong>{row.applicationStatus}</strong>
          </div>
          <div className="wx-ek-kv-row">
            <span>สมัครเมื่อ</span>
            <strong>{formatQueueTimestamp(row)}</strong>
          </div>
        </div>
      </section>

      <section className="wx-ek-dsec">
        <h3>สายงานที่รับผิดชอบ</h3>
        <div className="wx-ek-kv">
          <div className="wx-ek-kv-row">
            <span>Agent</span>
            <strong>
              {row.agent?.agentCode ?? "-"} · {row.agent?.displayName ?? "-"}
            </strong>
          </div>
          <div className="wx-ek-kv-row">
            <span>Super Agent</span>
            <strong>{row.line?.superAgentName ?? "-"}</strong>
          </div>
          <div className="wx-ek-kv-row">
            <span>PLC</span>
            <strong>{row.line?.plcName ?? "-"}</strong>
          </div>
          <div className="wx-ek-kv-row">
            <span>ที่มา</span>
            {/* Presence only - an invite code is never returned by any route. */}
            <strong>{row.hasInviteCard ? "มาจากบัตรเชิญของ Agent" : "สมัครเองผ่านลิงก์สาธารณะ"}</strong>
          </div>
        </div>
      </section>


      <section className="wx-ek-dsec">
        <h3>เอกสารที่มีอยู่</h3>
        <div className="wx-ek-kv">
          <div className="wx-ek-kv-row">
            <span>เลขหนังสือเดินทาง</span>
            <strong>{row.hasPassportNumber ? "มีในระบบ" : "ยังไม่มี"}</strong>
          </div>
          <div className="wx-ek-kv-row">
            <span>ไฟล์หนังสือเดินทาง</span>
            <strong>{row.hasPassportDocument ? "อัปโหลดแล้ว" : "ยังไม่อัปโหลด"}</strong>
          </div>
        </div>
      </section>

    </div>
  );
}

// P157: exported so the Agent Workers e-KYC tab can reuse the exact same rich
// review drawer (documents, risk, checklist, decision) the sysadmin queue uses -
// "kyc ... ไปเอามาจาก sysadmin". The controller that drives it is shared via
// EkycReviewDrawer.jsx; this stays the pure view.
export function EkycReviewDrawerContent({
  state,
  titleId,
  activeTab,
  onTabChange,
  decisionChoice,
  onSelectDecisionChoice,
  reasonText,
  onReasonChange,
  decisionValidationError,
  decisionSubmit,
  onSubmitDecision, createProfileState, onCreateWorkerProfile, onProfileSaved,
  queueRow, onStartEkyc, startState, checkResults, onCheckChange,
}) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด e-KYC..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-ek-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  // P149: a row with no review record has no detail endpoint to call. It is
  // still real work with a real person behind it, so the drawer renders what
  // the queue already knows and offers the action that creates the record -
  // rather than an error or an empty panel.
  if (!state.data) {
    return <EkycQueueOnlyDrawer titleId={titleId} row={queueRow} onStartEkyc={onStartEkyc} startState={startState} />;
  }

  const detail = state.data;
  const meta = statusMeta(detail.reviewStatus);

  // P149-R: one resolved state drives the panel, the decision block and the
  // approve gate together.
  const drawerState = resolveDrawerState(detail.reviewStatus, queueRow?.applicationStatus);
  const documentBlocker = resolveDocumentBlocker(detail, queueRow);
  const approvedWorkerProfileId = detail.workerProfileId ?? createProfileState?.workerProfileId ?? null;

  return (
    <div className="wx-ek-drawer">
      <span className="wx-ek-drawer-kicker">EKYC REVIEW DETAIL</span>
      <h2 id={titleId} className="wx-ek-drawer-title">
        รายละเอียด e-KYC
      </h2>
      <p className="wx-ek-drawer-sub">
        ใบสมัคร {detail.applicationId} · ส่งตรวจเมื่อ {formatAgeHours(detail.ageHours)}
      </p>
      {/* P149-R: what to do next, before any identity data - a reviewer should
          not have to read the record to find out whether it needs them. */}
      <DrawerNextActionPanel
        state={drawerState}
        approvedWorkerProfileId={approvedWorkerProfileId}
        documentBlocker={documentBlocker}
        onCreateWorkerProfile={onCreateWorkerProfile}
        onOpenWorkerProfile={onProfileSaved}
        createProfileState={createProfileState}
        reason={detail.decisionReason ?? detail.correctionReason}
      />

      {documentBlocker && (
        <section className="wx-ek-dsec wx-ek-doc-blocker" role="note">
          <h3>เอกสารยังไม่พร้อมสำหรับตรวจ e-KYC</h3>
          <ul className="wx-ek-doc-missing">
            {documentBlocker.missing.map((item) => (
              <li key={item.key}>
                <strong>{item.label}</strong>
                <span>{item.owner}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="wx-ek-drawer-status">
        <span className={`wx-badge wx-badge--${meta.badge}`}>{meta.label}</span>
        <span className={`wx-ek-risk wx-ek-risk--${riskVariant(detail.riskLevel)}`}>Risk: {riskLabel(detail.riskLevel)}</span>
        {/* Fixed for this packet, not gated on detail.stage.readyForMatch -
            Worker Profile creation and matching are not implemented here
            regardless of what any future backend value might say, so this
            is never suppressed. */}
        <span className="wx-badge wx-badge--danger">{NOT_READY_FOR_MATCH_TEXT}</span>
      </div>

      {/* P149-R2: no tabs. Splitting identity from decision meant a reviewer
          judged on one screen what they read on another; now the record and
          the check sit in one scroll. */}

      <EkycDetailsPane detail={detail} />

      {(
        <EkycDecisionPane
          detail={detail}
          checkResults={checkResults}
          onCheckChange={onCheckChange}
          documentBlocker={documentBlocker}
          decisionChoice={decisionChoice}
          onSelectDecisionChoice={onSelectDecisionChoice}
          reasonText={reasonText}
          onReasonChange={onReasonChange}
          decisionValidationError={decisionValidationError}
          decisionSubmit={decisionSubmit}
          onSubmitDecision={onSubmitDecision}
          createProfileState={createProfileState}
          onCreateWorkerProfile={onCreateWorkerProfile}
          onProfileSaved={onProfileSaved}
        />
      )}
    </div>
  );
}

function EkycDetailsPane({ detail }) {
  const docSummary = detail.documentSummary ?? {};
  const timelineSteps = [
    ...(detail.timeline ?? []).map((entry) => ({ key: entry.stageCode, label: entry.label, note: formatDateTimeThShort(entry.occurredAtUtc), state: "done" })),
    { key: detail.stage?.code ?? "current", label: detail.stage?.label ?? statusMeta(detail.reviewStatus).label, state: "current" },
    { key: "WorkerProfile", label: "สร้าง Worker Profile — ล็อกอยู่", note: "จะปลดล็อกหลังผ่าน e-KYC และมีขั้นตอนถัดไปเท่านั้น", state: "locked" },
  ];

  return (
    <>
      <section className="wx-ek-dsec wx-ek-dsec-row">
        <div className="wx-ek-dsec-col">
          <h3>ข้อมูลแรงงาน</h3>
          <div className="wx-ek-kv-row">
            <span>ชื่อ</span>
            <strong>{detail.worker?.fullNameNative || detail.worker?.fullNameEnglish || "-"}</strong>
          </div>
          <div className="wx-ek-kv-row">
            <span>สัญชาติ</span>
            <strong>{detail.worker?.nationality ?? "-"}</strong>
          </div>
          <div className="wx-ek-kv-row">
            <span>เบอร์โทร</span>
            <strong>{detail.worker?.phoneNumber ?? "-"}</strong>
          </div>
          <div className="wx-ek-kv-row">
            <span>ภาษา</span>
            <strong>{detail.worker?.preferredLanguageCode ?? "-"}</strong>
          </div>
        </div>
        <div className="wx-ek-dsec-col">
          <h3>Agent และสายงาน</h3>
          <div className="wx-ek-kv-row">
            <span>Agent</span>
            <strong>
              {detail.agent?.agentCode ?? "-"} · {detail.agent?.displayName ?? "-"}
            </strong>
          </div>
          <div className="wx-ek-kv-row">
            <span>Super Agent</span>
            <strong>{detail.line?.superAgentName ?? "-"}</strong>
          </div>
          <div className="wx-ek-kv-row">
            <span>PLC / บมจ</span>
            <strong>{detail.line?.plcName ?? "-"}</strong>
          </div>
        </div>
      </section>

      <section className="wx-ek-dsec">
        <h3>สถานะ e-KYC และไทม์ไลน์</h3>
        <ol className="wx-ek-timeline">
          {timelineSteps.map((step) => (
            <li key={step.key} className={`wx-ek-timeline-step wx-ek-timeline-step--${step.state}`}>
              <span className="wx-ek-timeline-dot" aria-hidden="true" />
              <strong>{step.label}</strong>
              {step.note && <small>{step.note}</small>}
            </li>
          ))}
        </ol>
      </section>

      <section className="wx-ek-dsec">
        <h3>สรุปเอกสาร</h3>
        <div className="wx-ek-doc-summary">
          <div>
            <strong>{docSummary.requiredCount ?? "-"}</strong>
            <span>ต้องส่ง</span>
          </div>
          <div>
            <strong>{docSummary.submittedCount ?? "-"}</strong>
            <span>ส่งแล้ว</span>
          </div>
          <div className="wx-ek-doc-summary-ok">
            <strong>{docSummary.acceptedCount ?? "-"}</strong>
            <span>ผ่านแล้ว</span>
          </div>
          <div className="wx-ek-doc-summary-bad">
            <strong>{docSummary.rejectedCount ?? "-"}</strong>
            <span>ถูกปฏิเสธ</span>
          </div>
          <div className="wx-ek-doc-summary-warn">
            <strong>{docSummary.needsCorrectionCount ?? "-"}</strong>
            <span>ต้องแก้ไข</span>
          </div>
        </div>
      </section>

      <section className="wx-ek-dsec">
        <h3>รายการเอกสาร</h3>
        {(detail.documents ?? []).length === 0 ? (
          <p className="wx-state-body">ยังไม่มีเอกสารสำหรับรายการนี้</p>
        ) : (
          <div className="wx-ek-doc-list">
            {detail.documents.map((doc, index) => {
              const dMeta = docStatusMeta(doc.documentStatus);
              return (
                <div className="wx-ek-doc-item" key={`${doc.documentType}-${index}`}>
                  <div className="wx-ek-doc-item-top">
                    <strong>{DOC_TYPE_LABELS[doc.documentType] ?? doc.documentType}</strong>
                    <span className={`wx-badge wx-badge--${dMeta.badge}`}>{dMeta.label}</span>
                  </div>
                  <div className="wx-ek-doc-item-grid">
                    <div>
                      <label>ไฟล์</label>
                      <strong title={doc.fileName ?? undefined}>{doc.fileName ?? "-"}</strong>
                    </div>
                    <div>
                      <label>ชนิดไฟล์</label>
                      <strong title={doc.contentType ?? undefined}>{doc.contentType ?? "-"}</strong>
                    </div>
                    <div>
                      {/* Never the raw document number - only the backend-masked value. */}
                      <label>เลขเอกสาร</label>
                      <strong className="wx-ek-code">{doc.maskedDocumentNumber ?? "— ไม่มี —"}</strong>
                    </div>
                    <div>
                      {/* Never StorageObjectKey/binary/signed URL - only this yes/no indicator. */}
                      <label>ไฟล์แนบ</label>
                      <strong className={doc.hasStorageObjectKey ? "wx-ek-hasfile-yes" : "wx-ek-hasfile-no"}>
                        {doc.hasStorageObjectKey ? "มี" : "ไม่มี"}
                      </strong>
                    </div>
                    <div>
                      <label>ชื่อในเอกสาร</label>
                      <strong title={doc.extractedFullName ?? undefined}>{doc.extractedFullName ?? "— ไม่มี —"}</strong>
                    </div>
                    <div>
                      <label>สัญชาติ (OCR)</label>
                      <strong>{doc.extractedNationality ?? "— ไม่มี —"}</strong>
                    </div>
                    <div>
                      <label>วันเกิด (OCR)</label>
                      <strong>{doc.extractedDateOfBirth ?? "— ไม่มี —"}</strong>
                    </div>
                    <div>
                      <label>ตรวจเมื่อ</label>
                      <strong>{formatDateTimeTh(doc.reviewedAtUtc) ?? "-"}</strong>
                    </div>
                  </div>
                  {doc.reviewerNote && <p className="wx-ek-note-muted wx-ek-note-muted--left">หมายเหตุ: {doc.reviewerNote}</p>}
                </div>
              );
            })}
          </div>
        )}
      </section>

    </>
  );
}

function EkycDecisionPane({
  detail,
  documentBlocker,
  checkResults,
  onCheckChange,
  decisionChoice,
  onSelectDecisionChoice,
  reasonText,
  onReasonChange,
  decisionValidationError,
  decisionSubmit,
  onSubmitDecision,
  createProfileState,
  onCreateWorkerProfile,
  onProfileSaved,
}) {
  const nextActions = detail.nextActions ?? {};
  const submitting = decisionSubmit.status === "submitting";

  // P148 lifecycle state, all read from the backend's own detail payload.
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const creatingProfile = createProfileState?.status === "submitting";
  const isApproved = detail.reviewStatus === "Approved";
  const alreadyHasProfile = Boolean(detail.workerProfileId ?? createProfileState?.workerProfileId);
  // Only an approved review can produce a profile - the service enforces this
  // too, so a stale UI can never talk the backend into an early creation.
  const canCreateProfile = isApproved && !alreadyHasProfile && nextActions.canCreateWorkerProfile !== false;
  const profileApplicationId = detail.applicationId ?? null;
  // อนุมัติ becomes available only when every item has been judged and passed.
  // This is the whole point of the checklist: an approval must mean someone
  // actually checked each thing, not that they pressed the green button.
  const checkOutcome = checklistOutcome(checkResults ?? {});

  const noPermissionAtAll = !nextActions.canApprove && !nextActions.canReject && !nextActions.canRequestCorrection;

  return (
    <>


      <EkycChecklist results={checkResults} onChange={onCheckChange} disabled={submitting} />

      <section className="wx-ek-dsec">
        <h3>บันทึกผลการตรวจ</h3>
        {/* P149-R: approving with the required data missing would record a
            verification that never happened - the checklist above names exactly
            what is missing and who supplies it. */}
        {documentBlocker && <p className="wx-ek-form-message">{APPROVE_BLOCKED_TEXT}</p>}
        {!documentBlocker && !checkOutcome.allPassed && (
          <p className="wx-ek-form-message">
            {checkOutcome.failed.length > 0
              ? "มีข้อที่ไม่ผ่าน จึงอนุมัติไม่ได้"
              : `ตรวจครบทุกข้อก่อนจึงจะอนุมัติได้ (${checkOutcome.judgedCount}/${checkOutcome.totalCount})`}
          </p>
        )}
        <div className="wx-ek-decision-options">
          <button
            type="button"
            className={`wx-ek-decision-btn wx-ek-decision-btn--approve${decisionChoice === "Approved" ? " wx-ek-decision-btn--selected" : ""}`}
            disabled={!nextActions.canApprove || submitting || Boolean(documentBlocker) || !checkOutcome.allPassed}
            onClick={() => onSelectDecisionChoice("Approved")}
          >
            อนุมัติ
            <small>Approve</small>
          </button>
          <button
            type="button"
            className={`wx-ek-decision-btn wx-ek-decision-btn--reject${decisionChoice === "Rejected" ? " wx-ek-decision-btn--selected" : ""}`}
            disabled={!nextActions.canReject || submitting}
            onClick={() => onSelectDecisionChoice("Rejected")}
          >
            ไม่อนุมัติ
            <small>Reject</small>
          </button>
          <button
            type="button"
            className={`wx-ek-decision-btn wx-ek-decision-btn--correction${decisionChoice === "NeedsCorrection" ? " wx-ek-decision-btn--selected" : ""}`}
            disabled={!nextActions.canRequestCorrection || submitting}
            onClick={() => onSelectDecisionChoice("NeedsCorrection")}
          >
            ให้แก้ไข
            <small>Request correction</small>
          </button>
        </div>

        {noPermissionAtAll && (
          <p className="wx-ek-reserved-note" role="status">
            ไม่มีสิทธิ์หรือยังไม่สามารถตัดสินใจสำหรับรายการนี้ได้ในสถานะปัจจุบัน
          </p>
        )}

        <label className="wx-ek-field-label" htmlFor="ekycReasonBox">
          เหตุผล (บังคับกรอก)
        </label>
        <textarea
          id="ekycReasonBox"
          className="wx-ek-reason-box"
          rows={4}
          value={reasonText}
          onChange={(event) => onReasonChange(event.target.value)}
          placeholder="ระบุเหตุผลของการตัดสินใจ เช่น เอกสารครบและตรงกับตัวแรงงาน"
        />

        {decisionValidationError && (
          <p className="wx-ek-form-message" role="alert">
            {decisionValidationError}
          </p>
        )}
        {decisionSubmit.status === "error" && (
          <p className="wx-ek-form-message" role="alert">
            {describeDecisionError(decisionSubmit.error)}
          </p>
        )}
        {decisionSubmit.status === "success" && (
          <p className="wx-ek-form-success" role="status">
            บันทึกการตัดสินใจสำเร็จแล้ว
          </p>
        )}

        <div className="wx-ek-notice">{WORKER_PROFILE_WARNING_TEXT}</div>

        <button type="button" className="wx-button wx-button--primary wx-ek-submit-decision" disabled={submitting} onClick={onSubmitDecision}>
          {submitting ? "กำลังบันทึก..." : "บันทึกการตัดสินใจ"}
        </button>
      </section>

      <section className="wx-ek-dsec">
        <h3>ขั้นตอนถัดไปหลังอนุมัติ</h3>
        {/* P148: a real action, not a disabled placeholder. The endpoint
            behind it has existed since Prompt 097; nothing ever called it,
            so an approved review had no way forward. The backend still
            decides who may do this and refuses any review that is not
            actually approved. */}
        {alreadyHasProfile ? (
          <>
            <p className="wx-ek-notice">{PROFILE_CREATED_TEXT}</p>
            <button type="button" className="wx-button wx-button--ghost" onClick={() => setProfileDialogOpen(true)}>
              {FILL_PROFILE_BUTTON_TEXT}
            </button>
          </>
        ) : (
          <p className="wx-ek-notice">
            {canCreateProfile ? PROFILE_ACTION_MOVED_TEXT : PROFILE_ONLY_AFTER_APPROVAL_TEXT}
          </p>
        )}

        {createProfileState?.status === "error" && (
          <p className="wx-ek-form-message" role="alert">
            {createProfileState.message ?? CREATE_PROFILE_ERROR_TEXT}
          </p>
        )}

        {profileApplicationId && (
          <WorkerDetailedProfileDialog
            open={profileDialogOpen}
            applicationId={profileApplicationId}
            onClose={() => setProfileDialogOpen(false)}
            onSaved={onProfileSaved}
          />
        )}
      </section>
    </>
  );
}
